
import { hideAll } from 'tippy.js'
import Radio from '@/components/UntitledUI/Radio.vue'
import Icon from '@/components/UntitledUI/Icon.vue'
import Tippy from '@/components/common/Tippy.vue'
import { ObserveVisibility } from 'vue-observe-visibility'
import PolygonPointer from '@/components/FileViewer/Icons/PolygonPointer.vue'
import InfoNote from '@/components/FileViewer/InfoNote.vue'
import ScreenUtils from './Mixins/ScreenUtils.js'
import DispatchEventUtils from './Mixins/DispatchEventUtils.js'
import PageContent from './PageContent.vue'
import PencilSquareIcon from './Icons/PencilSquareIcon.vue'
import NotificationsIcon from './Icons/NotificationsIcon.vue'
import ShareIcon from './Icons/ShareIcon.vue'
import CopyButton from './CopyButton.vue'
import PagePdfContent from './PagePdfContent.vue'
import LoginRequired from './LoginRequired.vue'
import Avatar from './Avatar.vue'
import Collapsable from './Collapsable.vue'
import ChevronDownIcon from './Icons/ChevronDownIcon.vue'
import ChevronUpIcon from './Icons/ChevronUpIcon.vue'

export default {
    name: 'Page',
    directives: {
        ObserveVisibility
    },
    components: {
        InfoNote,
        ShareIcon,
        ChevronUpIcon,
        ChevronDownIcon,
        LoginRequired,
        PageContent,
        CopyButton,
        PencilSquareIcon,
        NotificationsIcon,
        PagePdfContent,
        Tippy,
        Avatar,
        Collapsable,
        Radio,
        Icon,
        PolygonPointer
    },
    mixins: [ScreenUtils, DispatchEventUtils],
    inject: [
        'linkClick',
        'savePage',
        'isLogged',
        'meta',
        'fvAPI_LoadPages',
        'fvAPI_SearchInFile',
        'fvAPI_HighlightCreate',
        'fvAPI_HighlightUpdate',
        'fvAPI_HighlightDelete',
        'user'
    ],
    props: {
        source: Object,
        zoomBy: Number,
        isText: Boolean,
        isAllRegsOpen: Boolean,
        highlights: {
            type: Array,
            default: () => []
        },
        isPopup: {
            type: Boolean,
            default: false
        },
        pointer: {
            type: Number,
            default: 0
        }
    },
    data () {
        return {
            isPageVisible: false,
            selectedAvatarId: null,
            transType: this.source.locale,
            section: null,
            showLinks: false,
            showDown: false,
            showUp: false,
            el: null
        }
    },
    computed: {
        isHighlightAPIsSet () {
            return this.fvAPI_HighlightCreate && this.fvAPI_HighlightUpdate && this.fvAPI_HighlightDelete
        },
        highlightedUsers () {
            const users = this.highlights.filter(h => h.page_id === this.source.id)
                .filter(h => h?.user?.id && h.user.id !== this.user?.id)
                .map(h => h.user)
            return [...new Map(users.map(user => [user.id, user])).values()]
        },
        isAllRegualtionsOpen () {
            if (!this.source.selectedRegulations) {
                return false
            }
            return this.source.selectedRegulations && this.source.regulation.some(i => this.source.selectedRegulations.includes(i.page_id))
        },
        isAllExplanationsOpen () {
            if (!this.source.selectedExplanations) {
                return false
            }
            return this.source.selectedExplanations && this.source.explanation.some(i => this.source.selectedExplanations.includes(i.page_id))
        },
        subSections () {
            if (!this.isLogged) {
                return this.fakeSubSections
            }

            let regulations = []
            if (this.source.regulation) {
                regulations = Object.values(this.groupByKey(this.source.regulation, 'hash'))

                regulations.sort((a, b) => Math.min(...a.map(el => el.order)) - Math.min(...b.map(el => el.order)))
                regulations = regulations.map(r => r.sort((a, b) => a.order - b.order))
            }

            let explanations = []
            if (this.source.explanation) {
                explanations = Object.values(this.groupByKey(this.source.explanation, 'hash'))

                explanations.sort((a, b) => Math.min(...a.map(el => el.order)) - Math.min(...b.map(el => el.order)))
                explanations = explanations.map(r => r.sort((a, b) => a.order - b.order))
            }

            return [
                {
                    title: this.$t('اللوائح'),
                    list: regulations
                },
                {
                    title: this.$t('الشروحات'),
                    list: explanations
                }
            ]
        },
        fakeSubSections () {
            const fakeSections = []
            if (this.source.regulation) {
                fakeSections.push({
                    title: this.$t('اللوائح'),
                    list: [
                        {
                            file_name: this.$t('اسم اللائحة'),
                            title: this.$t('عنوان اللائحة'),
                            content: this.$t('محتوى اللائحة')
                        }
                    ]
                })
            }
            if (this.source.explanation) {
                fakeSections.push({
                    title: this.$t('الشروحات'),
                    list: [
                        {
                            file_name: this.$t('اسم الشرح'),
                            title: this.$t('عنوان الشرح'),
                            content: this.$t('محتوى الشرح')
                        }
                    ]
                })
            }

            return fakeSections
        },
        locales () {
            const locales = {}
            for (const locale of this.source.locales ?? []) {
                locales[locale] = this.meta.locales.find(loc => loc.code === locale)
            }
            return locales
        },
        translations () {
            return this.meta.locales.filter(locale => this.locales[locale.code] !== undefined).filter(locale => locale.code.startsWith(this.$helpers.getLocale()))
        },
        otherTranslations () {
            return this.meta.locales.filter(locale => this.locales[locale.code] !== undefined).filter(locale => !locale.code.startsWith(this.$helpers.getLocale()))
        },
        sourceDir () {
            return this.locales[this.source.locale]?.dir ?? 'rtl'
        },
        getSectionTranslationIcon () {
            return this.otherTranslations.length > 0 ? this.otherTranslations[0].icon : 'global'
        },
        showPointer () {
            return this.pointer === this.source.id
        }
    },
    watch: {
        isAllRegsOpen () {
            if (this.isLogged && this.source.regulation) {
                this.dispatch('FileViewer',
                    'show-regulation',
                    {
                        page: this.source,
                        idOfTheSelected: this.isAllRegsOpen ? this.source.regulation.map(r => r.page_id) : []
                    })
            }
        },
        transType () {
            this.dispatch('FileViewer', 'set-locale', {
                page: this.source,
                locale: this.transType
            })
        }
    },
    mounted () {
        this.el = this.$el
    },
    created () {
        this.selectedAvatarId = this.user?.id
        this.section = this.source
        if (this.isLogged && this.isAllRegsOpen && this.source.regulation) {
            this.dispatch('FileViewer',
                'show-regulation',
                { page: this.source, idOfTheSelected: this.source.regulation.map(r => r.page_id) })
        }
    },
    methods: {
        groupByKey (arr, key) {
            return arr.reduce(function (r, a) {
                r[a[key]] = r[a[key]] || []
                r[a[key]].push(a)
                return r
            }, Object.create(null))
        },
        toggleAvatar (user) {
            if (!user?.id) {
                user = this.user
            }
            this.selectedAvatarId = this.selectedAvatarId === user.id ? this.user?.id : user.id
        },
        toggleSection (title) {
            const eventName = title === this.$t('الشروحات') ? 'show-explanation' : 'show-regulation'
            const isAllOpen = title === this.$t('الشروحات') ? this.isAllExplanationsOpen : this.isAllRegualtionsOpen
            if (isAllOpen) {
                this.dispatch('FileViewer',
                    eventName,
                    { page: this.source, idOfTheSelected: [] })
            } else {
                this.dispatch('FileViewer',
                    eventName,
                    {
                        page: this.source,
                        idOfTheSelected: this.subSections.find(s => s.title === title).list.flatMap(l => l.map(i => i.page_id))
                    })
            }
        },
        toggleSubSection (title, idOfTheSelected) {
            const eventName = title === this.$t('الشروحات') ? 'show-explanation' : 'show-regulation'
            this.dispatch('FileViewer', eventName, { page: this.source, idOfTheSelected })
        },
        switchTransType (code) {
            if (code.startsWith(this.$helpers.getLocale())) {
                this.transType = code
            } else {
                this.showOtherTranslations()
            }
            hideAll()
        },
        showOtherTranslations () {
            this.dispatch('FileViewer', 'show-other-locale', { page: this.source })
        }
    }
}
